import { capitalize } from 'lodash-es';

export const pythonReservedWords = new Set([
  'False',
  'def',
  'if',
  'raise',
  'None',
  'del',
  'import',
  'return',
  'True',
  'elif',
  'in',
  'try',
  'and',
  'else',
  'is',
  'while',
  'as',
  'except',
  'lambda',
  'with',
  'assert',
  'finally',
  'nonlocal',
  'yield',
  'break',
  'for',
  'not',
  'class',
  'from',
  'or',
  'continue',
  'global',
  'pass',
  'async',
  'await',
]);

export const upCaseFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const truncateFileName = (
  fileName: string,
  maxFileNameLength: number,
  cleanupRegex: RegExp,
): string => {
  const cleanedFileName = fileName.replace(cleanupRegex, '');
  const fileNameLength = cleanedFileName.length;
  if (fileNameLength < maxFileNameLength) return cleanedFileName;

  const frontChars = cleanedFileName.substring(0, maxFileNameLength / 2).trim();
  const lastChars = cleanedFileName
    .substring(fileNameLength - maxFileNameLength / 2, fileNameLength)
    .trim();

  return `${frontChars}...${lastChars}`;
};

export const snakeCaseToCapitaliseWords = (str: string) => {
  return str
    .split('_')
    .map(word => capitalize(word))
    .join(' ');
};

// Checks if a string is a valid E.164 number
// See - https://www.twilio.com/docs/glossary/what-e164#:~:text=This%20number%20allows%20phone%20calls,a%20maximum%20of%20fifteen%20digits.

export const isStringValidE164Format = (string: string) => {
  const regex = new RegExp(/^\+[1-9]\d{1,14}$/);

  const result = regex.test(string);

  return result;
};

export const formatChatMessage = (message: string) => {
  const matchNumberSequenceAtEndOfSentence = new RegExp(/(\s+\d+\. )/gm);
  return message.replaceAll(matchNumberSequenceAtEndOfSentence, '\n$1');
};

const escapeRegExp = (text: string) =>
  text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

export const getPercentageMatch = (search: string, text: string) => {
  const regex = new RegExp(escapeRegExp(search), 'gi');
  const numMatches = (text.match(regex) || []).length;

  if (!numMatches) {
    return 0;
  }

  return (search.length * numMatches) / text.length;
};

export const validatePythonVariable = (value: string) => {
  if (!value) {
    return 'Please provide a value';
  }
  if (!/^[a-z0-9_]+$/i.test(value)) {
    return 'Value can only contain alphanumeric characters and underscores';
  }
  if (/^[0-9]*$/.test(value[0])) {
    return "Value can't start with a number";
  }
  if (pythonReservedWords.has(value)) {
    return `${value} is a reserved word`;
  }
  return true;
};

export const isValidPythonVariable = (value: string): boolean =>
  validatePythonVariable(value) === true;

/**
 * Converts a string to a valid Python identifier
 * Replaces spaces and special characters with underscores, converts to lowercase
 * @param {string} name - The string to convert
 * @return {string} A valid Python identifier
 */
export const toPythonicIdentifier = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_') // Replace non-alphanumeric (except underscore) with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with single underscore
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
};

export const getUniqueFunctionName = (
  name: string,
  functionNames: string[],
) => {
  const nameSet = new Set(functionNames);

  let suffixNum = 1;
  let uniqueName = name;

  while (nameSet.has(uniqueName)) {
    uniqueName = `${name}_${suffixNum}`;
    suffixNum++;
  }

  return uniqueName;
};

export const pluralise = (str: string, count: number) =>
  `${str}${count === 1 ? '' : 's'}`;

export const getEmoji = (hex: string) =>
  String.fromCodePoint(parseInt(hex, 16));

//  @example
//  maskString('sk-1234567890abcdef1234567890abcdef')
//  Returns: 'sk-1************cdef'
//  @example
//  maskString('short', { minLength: 10 })
//  Returns: 'short' (unchanged because it's shorter than minLength)
export const maskString = (
  str: string,
  options: {
    startChars?: number;
    endChars?: number;
    maskChar?: string;
    maskLength?: number;
    minLength?: number;
  } = {},
): string => {
  const {
    startChars = 4,
    endChars = 4,
    maskChar = '*',
    maskLength = 12,
    minLength = 8,
  } = options;

  if (str.length <= minLength) {
    return str;
  }

  const start = str.slice(0, startChars);
  const end = str.slice(-endChars);
  const maskedSection = maskChar.repeat(maskLength);

  return `${start}${maskedSection}${end}`;
};
